import React from "react";
import {
  MessageSquare,
  Image as ImageIcon,
  Mic,
  Wrench,
  Send,
  Plus,
  ChevronDown,
  Wifi,
  Signal,
  BatteryFull,
} from "lucide-react";

/* Stylized JedBot glyph (tri-node mark) rendered as SVG for crispness + glow. */
export const JedGlyph = ({ size = 28, className = "" }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} className={className} aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M24 15 L24 27" />
      <path d="M24 27 L15 33" />
      <path d="M24 27 L33 33" />
    </g>
    <circle cx="24" cy="12" r="4.4" fill="currentColor" />
    <circle cx="13" cy="35" r="4.4" fill="currentColor" />
    <circle cx="35" cy="35" r="4.4" fill="currentColor" />
  </svg>
);

const StatusBar = ({ time = "9:41" }) => (
  <div className="flex items-center justify-between px-5 pt-3 text-[10px] font-medium text-white/70">
    <span>{time}</span>
    <div className="flex items-center gap-1">
      <Signal className="h-3 w-3" />
      <Wifi className="h-3 w-3" />
      <BatteryFull className="h-3.5 w-3.5" />
    </div>
  </div>
);

/* Reusable device shell: bezel, notch, side buttons, reflection */
export const PhoneShell = ({ children, className = "", testid }) => (
  <div
    className={`relative aspect-[9/19] rounded-[42px] border border-white/15 bg-[#0A0F1E] shadow-[0_30px_90px_rgba(0,0,0,0.65)] ${className}`}
    data-testid={testid}
  >
    {/* side buttons */}
    <div className="absolute -left-[2px] top-24 h-10 w-[3px] rounded-l bg-white/20" />
    <div className="absolute -left-[2px] top-36 h-16 w-[3px] rounded-l bg-white/20" />
    <div className="absolute -right-[2px] top-28 h-20 w-[3px] rounded-r bg-white/20" />
    {/* inner screen */}
    <div className="absolute inset-[9px] overflow-hidden rounded-[34px] bg-[#070A12]">
      {/* notch */}
      <div className="absolute left-1/2 top-[8px] z-20 h-[24px] w-[112px] -translate-x-1/2 rounded-b-[16px] rounded-t-[6px] bg-[#050812] border border-white/10" />
      {/* screen reflection */}
      <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(480px_200px_at_22%_8%,rgba(255,255,255,0.10),transparent_55%)]" />
      <div className="relative z-0 flex h-full flex-col">{children}</div>
    </div>
  </div>
);

const CapTile = ({ icon: Icon, title, sub }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-3 transition-colors hover:border-white/20">
    <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-xl bg-[rgba(99,179,255,0.14)] text-[#8FCBFF]">
      <Icon className="h-4 w-4" />
    </div>
    <p className="text-[12px] font-semibold text-white">{title}</p>
    <p className="text-[10px] leading-tight text-white/55">{sub}</p>
  </div>
);

/* ---------- Screen 1: AI assistant ---------- */
export const AssistantScreen = () => (
  <div
    className="flex h-full flex-col"
    style={{
      background:
        "radial-gradient(700px 420px at 70% 0%, rgba(99,179,255,0.20), transparent 55%), radial-gradient(520px 320px at 20% 30%, rgba(167,179,255,0.12), transparent 60%)",
    }}
    data-testid="phone-mock-screen-1"
  >
    <StatusBar />
    <div className="flex items-center gap-2 px-5 pt-4">
      <div className="relative flex h-6 w-6 items-center justify-center">
        <span className="absolute inset-0 rounded-full bg-[#63B3FF]/30 blur-md" />
        <JedGlyph size={18} className="relative text-[#8FCBFF]" />
      </div>
      <span className="text-[13px] font-semibold tracking-tight text-white">JedBot</span>
    </div>

    {/* greeting + glowing orb */}
    <div className="px-5 pt-4 text-center">
      <p className="text-[11px] text-white/60">Good morning,</p>
      <p className="mt-1 text-[14px] font-semibold leading-snug text-white">
        Let’s build something great<br />today with JedBot ✨
      </p>
    </div>
    <div className="flex justify-center py-3">
      <div className="relative flex h-16 w-16 items-center justify-center">
        <span className="absolute inset-0 rounded-full bg-[#63B3FF]/25 blur-2xl" />
        <span className="absolute inset-1 rounded-full border border-[#63B3FF]/40" />
        <span className="absolute inset-4 rounded-full border border-[#63B3FF]/25" />
        <JedGlyph size={30} className="relative text-[#8FCBFF] drop-shadow-[0_0_10px_rgba(99,179,255,0.8)]" />
      </div>
    </div>

    {/* capability tiles */}
    <div className="grid grid-cols-2 gap-2 px-4">
      <CapTile icon={MessageSquare} title="Chat" sub="Ask anything" />
      <CapTile icon={ImageIcon} title="Image" sub="Create visuals" />
      <CapTile icon={Mic} title="Voice" sub="Talk with JedBot" />
      <CapTile icon={Wrench} title="Tools" sub="Plan, write, more" />
    </div>

    {/* input bar */}
    <div className="mt-auto px-4 pb-4 pt-3">
      <div className="flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.06] px-3 py-2">
        <Plus className="h-4 w-4 text-white/50" />
        <span className="flex-1 text-[11px] text-white/40">Message JedBot…</span>
        <Mic className="h-4 w-4 text-white/50" />
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#63B3FF] text-[#06101f]">
          <Send className="h-3.5 w-3.5" />
        </span>
      </div>
    </div>
  </div>
);

const StoreBadge = ({ label, sub }) => (
  <div className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.06] px-2.5 py-1.5">
    <div className="h-5 w-5 rounded-md bg-white/85" />
    <div className="leading-none">
      <p className="text-[7px] text-white/55">{sub}</p>
      <p className="text-[10px] font-semibold text-white">{label}</p>
    </div>
  </div>
);

/* ---------- Screen 2: Image generation ---------- */
export const ImageGenScreen = () => (
  <div className="flex h-full flex-col bg-[#070A12]" data-testid="phone-mock-screen-2">
    <StatusBar time="9:41" />
    <div className="flex items-center justify-between px-5 pt-5">
      <span className="text-[13px] font-semibold text-white">Image Generation</span>
      <span className="h-2 w-2 rounded-full bg-[#50E3C2] shadow-[0_0_8px_rgba(80,227,194,0.9)]" />
    </div>

    {/* controls */}
    <div className="flex items-center gap-2 px-5 pt-3">
      <span className="inline-flex items-center gap-1 rounded-full border border-white/12 bg-white/[0.05] px-2.5 py-1 text-[10px] text-white/75">
        Cinematic <ChevronDown className="h-3 w-3" />
      </span>
      <span className="inline-flex items-center gap-1 rounded-full border border-white/12 bg-white/[0.05] px-2.5 py-1 text-[10px] text-white/75">
        16 : 9 <ChevronDown className="h-3 w-3" />
      </span>
    </div>

    {/* generated image */}
    <div className="px-5 pt-3">
      <div className="overflow-hidden rounded-2xl border border-white/14 shadow-[0_18px_60px_rgba(0,0,0,0.55)]">
        <img
          src="/astronaut.jpg"
          alt="AI-generated cinematic astronaut"
          loading="lazy"
          width="600"
          height="420"
          className="h-[220px] w-full object-cover"
        />
      </div>
    </div>

    {/* prompt (disabled look) */}
    <div className="px-5 pt-3">
      <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
        <span className="flex-1 text-[10px] text-white/40">Astronaut on an alien shore, volumetric light…</span>
        <span className="rounded-lg bg-white/12 px-2 py-1 text-[9px] font-semibold text-white/60">Generate</span>
      </div>
    </div>

    {/* everywhere card */}
    <div className="mt-auto px-4 pb-5 pt-4">
      <div className="rounded-2xl border border-white/12 bg-white/[0.05] p-3">
        <p className="text-[12px] font-semibold text-white">Your AI, everywhere.</p>
        <p className="text-[10px] text-white/55">iOS &amp; Android</p>
        <div className="mt-2 flex gap-2">
          <StoreBadge sub="Coming soon on" label="App Store" />
          <StoreBadge sub="Coming soon on" label="Google Play" />
        </div>
      </div>
    </div>
  </div>
);

/* ---------- Dual phone composition ---------- */
export const DualPhones = () => (
  <div className="relative mx-auto w-full max-w-[520px]" data-testid="hero-phones">
    {/* ambient glow behind phones */}
    <div className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(99,179,255,0.22),transparent_70%)] blur-2xl" />
    <div className="relative flex items-center justify-center">
      {/* back phone (image gen) — visible on mobile too, scaled down */}
      <div className="jb-float-delayed relative z-0 mt-10 block w-[142px] -translate-x-2 rotate-[6deg] sm:mt-16 sm:w-[210px] sm:-translate-x-6">
        <PhoneShell testid="phone-back">
          <ImageGenScreen />
        </PhoneShell>
      </div>
      {/* front phone (assistant) */}
      <div className="jb-float relative z-10 -ml-8 w-[168px] sm:-ml-16 sm:w-[248px]">
        <PhoneShell testid="phone-front">
          <AssistantScreen />
        </PhoneShell>
      </div>
    </div>
  </div>
);
